import type { AccountEntity } from '@actual-app/core/types/models';

import { readThisWeekByBank } from '#components/mtxbudget/bankReads';

/**
 * Whether to show the account as failing to sync. MTXbudget: one good read a
 * week is enough, so a failure only shows once its bank has sent nothing
 * since Sunday.
 */
export function isAccountFailedSync(
  account: Pick<AccountEntity, 'bank_sync_status'> &
    Partial<Pick<AccountEntity, 'account_id' | 'last_sync'>>,
) {
  const status = account.bank_sync_status;
  return (
    status != null &&
    status !== 'ok' &&
    status !== 'pending' &&
    status !== 'sync-requested' &&
    !readThisWeekByBank({
      account_id: account.account_id ?? null,
      last_sync: account.last_sync ?? null,
    })
  );
}

export function getFailedSyncError(
  account: Pick<AccountEntity, 'bank_sync_status' | 'account_sync_source'>,
): { type: string; code: string } {
  switch (account.bank_sync_status) {
    case 'reauth-required':
      if (account.account_sync_source === 'simpleFin') {
        return { type: 'INVALID_ACCESS_TOKEN', code: 'INVALID_ACCESS_TOKEN' };
      }
      return { type: 'ITEM_ERROR', code: 'ITEM_LOGIN_REQUIRED' };
    case 'attention-required':
      return {
        type: 'ACCOUNT_NEEDS_ATTENTION',
        code: 'ACCOUNT_NEEDS_ATTENTION',
      };
    case 'rate-limit-exceeded':
      return { type: 'RATE_LIMIT_EXCEEDED', code: 'RATE_LIMIT_EXCEEDED' };
    case 'timed-out':
      return { type: 'TIMED_OUT', code: 'TIMED_OUT' };
    case 'account-missing':
      return { type: 'ACCOUNT_MISSING', code: 'ACCOUNT_MISSING' };
    case 'not-configured':
      if (account.account_sync_source === 'goCardless') {
        return {
          type: 'GOCARDLESS_NOT_CONFIGURED',
          code: 'GOCARDLESS_NOT_CONFIGURED',
        };
      }
      return { type: 'NOT_CONFIGURED', code: 'NOT_CONFIGURED' };
    default:
      return { type: 'SYNC_ERROR', code: 'SYNC_ERROR' };
  }
}
